import React, { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Link as MuiLink } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import theme from 'theme'
import { mapMessagesForDownload } from 'lib/messagesLib'
import {
  CAR_CHOICE_ACCEPTED,
  CAR_CHOICE_ACCEPTED_WITH_REPRICE,
} from 'lib/carChoicesLib'
import {
  ASSIGNMENT_STATE_NR,
  ASSIGNMENT_STATE_TEAM_OK,
  ASSIGNMENT_STATE_TEAM_OK_NR,
} from 'lib/assignmentStates'

import ConfirmBar from 'components/atoms/ConfirmBar'
import SearchField from 'components/atoms/SearchField'
import CarsList from 'components/atoms/CarsList'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import DownloadBar from 'components/atoms/DownloadBar'
import LogIcon from 'components/atoms/LogIcon'

import useCarsFilters from 'hooks/useCarsFilters'
import useLogs from 'hooks/useLogs'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useInputOutput from 'hooks/useInputOutput'

import { getAssignmentState } from '../Assignment/assignmentLib'
import AssignmentCarAdditionalDetails from '../Assignment/AssignmentCarAdditionalDetails'
import AssignmentCarDetails from '../AssignmentCarDetails'
import AssignmentConfirmationDialog from '../Assignment/AssignmentConfirmationDialog'

import AssignmentChoicesRecap from './AssignmentChoicesRecap'

function getRows(titlesRow, cars, t, session, partners) {
  return [
    titlesRow,
    ...cars.map((car) => {
      const finalAssignment = t(
        `assignment.excel.status.${
          session.agenda.assignment.state
        }.${getAssignmentState(car)}`
      )

      return [
        car.plate || '-',
        car.vin || '-',
        `${car.title}`,
        finalAssignment,
        car.finalPrice || '-',
        (car.assignedPartnerId &&
          partners.find(
            (partner) => partner.partnerId === car.assignedPartnerId
          )?.name) ||
          '-',
        `${car.acceptedChoiceTotal || 0}`,
        `${car.acceptedWithRepriceChoiceTotal || 0}`,
        ...partners.map((partner) =>
          car.acceptedChoiceList.includes(partner.partnerId)
            ? t(`carsToBuy.choice.${CAR_CHOICE_ACCEPTED}`)
            : car.acceptedWithRepriceChoiceList.includes(partner.partnerId)
            ? t(`carsToBuy.choice.${CAR_CHOICE_ACCEPTED_WITH_REPRICE}`)
            : '-'
        ),
        car.messages ? mapMessagesForDownload(car.messages, t, true) : '-',
      ]
    }),
  ]
}

export default function AssignmentCarAssignment({
  session,
  partners,
  cars,
  handleValidation,
  handleConfirm,
  readOnly,
  contentIsReadOnly,
  refetchList,
  setShowHeader,
}) {
  const { t } = useTranslation()

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const [showDetails, setShowDetails] = useState()
  const [filteredCars, setFilteredCars] = useState([])

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirm)

  useEffect(() => {
    setShowHeader(!showDetails)
  }, [setShowHeader, showDetails])

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setYearRange,
    setBrands,
    setModels,
    setFuels,
    setQuery,
    setAssignmentChoice: setFilteringChoice,
    setAssignmentType,
    setAssignmentPartners,
    filterCars,
    resetFilters,
  } = useCarsFilters({
    startDataFilters: {
      assignmentPartners: [
        { label: t('common.form.all'), value: '' },
        ...partners.map((partner) => ({
          label: partner.name,
          value: partner.partnerId,
        })),
      ],
    },
  })

  const { saveXls } = useInputOutput()

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [cars, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.plate'),
      t('common.excel.vin'),
      t('common.excel.name'),
      t(`assignment.excel.finalStatus.${session.agenda.assignment.state}`),
      t('common.excel.finalPrice'),
      t(`assignment.excel.finalPartner.${session.agenda.assignment.state}`),
      t('assignment.excel.acceptedChoice'),
      t('assignment.excel.acceptedWithRepriceChoice'),
      ...partners.map((partner) => partner.name),
      t('common.excel.messages'),
    ]

    const rows = getRows(titlesRow, cars, t, session, partners)

    saveXls('car-assignment', rows)
  }

  return showDetails ? (
    <AssignmentCarDetails
      vin={showDetails}
      session={session}
      cars={filteredCars}
      partners={partners}
      setShowDetails={setShowDetails}
      refetchList={refetchList}
      contentIsReadOnly={contentIsReadOnly}
    />
  ) : (
    <>
      <Box mt={1} data-testid="assignment-car" />
      <ConfirmBar
        label={t('assignment.car-assignment.confirmButton')}
        left={
          <SearchField
            data-testid="text-search"
            placeholder={t('common.form.searchBy')}
            value={filters.query}
            onChange={(event) => setQuery(event.target.value)}
          />
        }
        leftProps={{ sx: { width: 0.5 } }}
        right={
          <>
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_assignment_${session?.sessionId}`, {
                  area: t('common.logs.assignment'),
                  sessionId: session?.sessionId,
                })
              }
            />
            <DownloadBar handleDownload={handleDownload} />
          </>
        }
        top={
          <AssignmentChoicesRecap
            cars={cars}
            filters={filters}
            filtersData={filtersData}
            setAssignmentPartners={setAssignmentPartners}
            setFilteringChoice={setFilteringChoice}
            setAssignmentType={setAssignmentType}
          />
        }
        topProps={{
          sx: {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
        disabled={
          !handleValidation() ||
          !!session?.assignmentEndedAt ||
          readOnly ||
          contentIsReadOnly
        }
        handleConfirm={handleOpenConfirmationDialog}
        sticky
      />
      <CarsList
        left={
          <CarFiltersColumn
            filters={filters}
            filtersData={filtersData}
            filteredCars={
              filteredCars?.length !== cars.length ? filteredCars : undefined
            }
            setKmRange={setKmRange}
            setYearRange={setYearRange}
            setBrands={setBrands}
            setModels={setModels}
            setFuels={setFuels}
            resetFilters={resetFilters}
            readOnly={readOnly}
          />
        }
        right={
          <>
            {filteredCars.map((car, index) => (
              <CarCard
                data-testid={`car-${index}`}
                key={car.originalId}
                onClick={() => setShowDetails(car.vin)}
                height={theme.custom.carCardAdditionalMaxHeight}
                car={car}
                isAssignment
                fee={
                  [ASSIGNMENT_STATE_NR].includes(getAssignmentState(car))
                    ? undefined
                    : car.assignmentFee
                }
                price={
                  [
                    ASSIGNMENT_STATE_TEAM_OK_NR,
                    ASSIGNMENT_STATE_TEAM_OK,
                  ].includes(getAssignmentState(car)) &&
                  car.finalPrice &&
                  car.fee
                    ? car.finalPrice
                    : undefined
                }
                rightProps={{ justifyContent: 'flex-end' }}
                right={
                  <>
                    <MuiLink
                      color="primary"
                      data-testid="car-detail-right-click"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setShowDetails(car.vin)}
                      component={Box}
                      variant="body2"
                    >
                      <strong>{t('carsToBuy.details')}</strong>
                    </MuiLink>
                  </>
                }
                bottom={
                  <AssignmentCarAdditionalDetails
                    car={car}
                    partners={partners}
                    sessionState={session.agenda.assignment.state}
                  />
                }
              />
            ))}
          </>
        }
      />
      <AssignmentConfirmationDialog
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
    </>
  )
}

AssignmentCarAssignment.propTypes = {
  session: T.object.isRequired,
  partners: T.array,
  cars: T.array,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
